/**
 * What occupies the reserved 104 pt band, and in what order of precedence.
 *
 * ── Why this is a function rather than nested ternaries ──
 *
 * The band started as "the prediction row" and now has six possible tenants. Each arrived for a good reason and
 * each arrived *separately*, which is exactly how four components end up guessing at a precedence nobody wrote
 * down — and then two of them appear at once and overflow a band whose whole promise is that it never changes
 * height.
 *
 * ── Why the band has tenants at all (C65) ──
 *
 * The vertical stack is 761 pt of 768. There is no room for a new row, and the bottom bar is full at six targets
 * per row (C2's arithmetic). Phase 3 asks for five more person-facing targets — four repair phrases and a
 * one-action low-effort toggle — so they go where space already exists and is already reserved.
 *
 * The toggle has since left for care mode and the layer switch holds its slot, which is **not** a tenant and is
 * not named below: it has to be reachable while the candidates are showing, so it sits in the band's frame
 * rather than in this precedence (`GEO-21`, ADR-039). Everything here is still exactly one of six.
 *
 * The fit is better than a compromise. Repair phrases are used *between* utterances, precisely when the composer
 * is empty and there are no candidates. So the band means **"what to do next"**: candidates while composing,
 * conversation repair while not. Nothing is added, nothing moves, and the keyboard below is untouched — which is
 * the one guarantee this band exists to keep.
 */

export type BandTenant =
  /** An utterance was not heard; one tap retries, repairs or restarts (`SUR-23`). */
  | 'tapToSpeak'
  /** Words to complete what is being typed. */
  | 'candidates'
  /** First launch only: restore before setting up by hand (4.7). */
  | 'restoreOffer'
  /** A hold just cleared the sentence, and it can be brought back (`SUR-26`). */
  | 'undoClear'
  /** At rest: repair phrases and the low-effort toggle (3.7, 3.8). */
  | 'repair'
  | 'empty';

export interface BandInput {
  candidateCount: number;
  /** An utterance is waiting for a real gesture. */
  pendingSpeech: boolean;
  /** This launch created the database, and the offer has not been dismissed. */
  offerRestore: boolean;
  /** A hold cleared the buffer and the offer to bring it back has not expired. */
  offerUndoClear: boolean;
  /** Nothing typed. Which is what "between utterances" actually means. */
  composerEmpty: boolean;
}

/**
 * The precedence, in one place, with the reason for each step.
 *
 * 1. **tapToSpeak** beats everything, including candidates. After a failed utterance the composer still holds its
 *    text — `commit()` only runs on success — so candidates may well be showing. If they won, the person could
 *    not reach the one control that would let them be heard.
 * 2. **candidates**, because typing is the primary activity and the row is theirs by default.
 * 3. **restoreOffer**, which only exists on one launch and can wait for a pause in typing.
 * 4. **undoClear**, below the candidates deliberately: typing after a clear means the person accepted it, and
 *    the offer withdraws itself on the first keystroke anyway. Above **repair** because a sentence that has
 *    just been thrown away is more urgent than a phrase for between utterances — and the repair row is the
 *    right thing to displace for a few seconds, since both occupy the same "nothing is being typed" state.
 * 5. **repair**, when nothing is being typed. Not "no candidates" — an accepted candidate leaves text in the
 *    composer and no candidates, and mid-sentence is composing rather than repairing.
 */
export function bandTenant(input: BandInput): BandTenant {
  if (input.pendingSpeech) return 'tapToSpeak';
  if (input.candidateCount > 0) return 'candidates';
  if (input.offerRestore) return 'restoreOffer';
  if (input.offerUndoClear) return 'undoClear';
  if (input.composerEmpty) return 'repair';
  return 'empty';
}
